import React, { useState } from 'react';
import { Card, Input, Button, Form, message, Result } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, BankOutlined, IdcardOutlined, EyeOutlined, EyeInvisibleOutlined, CheckCircleOutlined } from '@ant-design/icons';

const API_BASE_URL = (() => {
  const hostname = window.location.hostname;
  const port = window.location.port;
  if (!port || port === '80' || port === '443') {
    return `${window.location.protocol}//${hostname}/api/v1`;
  }
  if (port === '3000') {
    return `http://${hostname}:8000/api/v1`;
  }
  return `http://${hostname}:${port}/api/v1`;
})();

function RegisterPage({ onRegisterSuccess, onSwitchToLogin }) {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  const [form] = Form.useForm();

  const handleRegister = async (values) => {
    if (values.password !== values.confirmPassword) {
      message.error('兩次輸入的密碼不一致');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: values.username,
          password: values.password,
          display_name: values.display_name,
          email: values.email || undefined,
          company_name: values.company_name || undefined,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Registration failed (HTTP ${response.status})`);
      }

      setPending(true);
    } catch (error) {
      message.error(error.message || '註冊失敗');
    } finally {
      setLoading(false);
    }
  };

  if (pending) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #001529 0%, #003a8c 50%, #0050b3 100%)',
      }}>
        <Card style={{ width: 440, borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: 'none' }}
              bodyStyle={{ padding: '40px 32px' }}>
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="註冊申請已送出"
            subTitle="您的帳號已建立，請等待管理員審核。審核通過後即可使用帳號登入。"
            extra={
              <Button type="primary" onClick={onSwitchToLogin}>
                返回登入頁
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #001529 0%, #003a8c 50%, #0050b3 100%)',
    }}>
      <Card
        style={{
          width: 440,
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          border: 'none',
        }}
        bodyStyle={{ padding: '40px 32px 32px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            fontSize: 36,
            fontWeight: 800,
            background: 'linear-gradient(135deg, #1890ff, #36cfc9)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 4,
          }}>
            SPO
          </div>
          <div style={{ color: '#666', fontSize: 14 }}>
            建立您的帳號
          </div>
        </div>

        <Form
          form={form}
          onFinish={handleRegister}
          size="large"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '請輸入帳號' },
              { min: 3, message: '帳號至少 3 個字元' },
            ]}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="帳號"
            />
          </Form.Item>

          <Form.Item
            name="display_name"
            rules={[{ required: true, message: '請輸入顯示名稱' }]}
          >
            <Input
              prefix={<IdcardOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="顯示名稱"
            />
          </Form.Item>

          <Form.Item name="email">
            <Input
              prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Email（選填）"
              type="email"
            />
          </Form.Item>

          <Form.Item name="company_name">
            <Input
              prefix={<BankOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="公司名稱（選填）"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '請輸入密碼' },
              { min: 6, message: '密碼至少 6 個字元' },
            ]}
          >
            <Input
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              type={showPassword ? 'text' : 'password'}
              placeholder="密碼"
              suffix={
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ cursor: 'pointer', color: '#bfbfbf' }}
                >
                  {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </span>
              }
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            rules={[{ required: true, message: '請再次輸入密碼' }]}
          >
            <Input
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              type={showPassword ? 'text' : 'password'}
              placeholder="確認密碼"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{
                height: 44,
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 16,
                background: 'linear-gradient(135deg, #1890ff, #096dd9)',
                border: 'none',
              }}
            >
              註冊
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <span style={{ color: '#999' }}>已有帳號？</span>{' '}
            <Button type="link" onClick={onSwitchToLogin} style={{ padding: 0 }}>
              登入
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
}

export default RegisterPage;
